import { forwardRef, type ButtonHTMLAttributes } from 'react'
import { cn } from '@/lib/utils'
import { Spinner } from './Spinner'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variante?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'outline'
  tamano?: 'sm' | 'md' | 'lg'
  cargando?: boolean
  icono?: React.ReactNode
  iconoDerecha?: boolean
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variante = 'primary',
      tamano = 'md',
      cargando = false,
      icono,
      iconoDerecha = false,
      children,
      className,
      disabled,
      ...props
    },
    ref
  ) => {
    const base =
      'inline-flex items-center justify-center gap-2 font-medium rounded-md transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-[#030712] disabled:opacity-50 disabled:cursor-not-allowed select-none'

    const variantes = {
      primary:
        'bg-teal-700 hover:bg-teal-600 active:bg-teal-800 text-white focus-visible:ring-teal-600',
      secondary:
        'bg-white/[0.06] hover:bg-white/[0.09] active:bg-white/[0.04] text-white/75 border border-white/[0.08] hover:border-white/[0.14] focus-visible:ring-white/20',
      ghost:
        'bg-transparent hover:bg-white/[0.05] active:bg-white/[0.03] text-white/45 hover:text-white/78 focus-visible:ring-white/20',
      danger:
        'bg-red-900/70 hover:bg-red-800/80 active:bg-red-900 text-red-200 border border-red-800/50 focus-visible:ring-red-700',
      outline:
        'bg-transparent border border-white/[0.1] hover:border-white/[0.18] hover:bg-white/[0.04] text-white/48 hover:text-white/75 focus-visible:ring-white/20',
    }

    const tamanos = {
      sm: 'text-[11px] px-2.5 py-1.5 h-7',
      md: 'text-[12.5px] px-4 py-2 h-9',
      lg: 'text-sm px-5 py-2.5 h-10',
    }

    return (
      <button
        ref={ref}
        disabled={disabled || cargando}
        className={cn(base, variantes[variante], tamanos[tamano], className)}
        {...props}
      >
        {cargando ? (
          <Spinner tamano={tamano === 'lg' ? 'md' : 'sm'} />
        ) : (
          !iconoDerecha && icono
        )}
        {children}
        {!cargando && iconoDerecha && icono}
      </button>
    )
  }
)

Button.displayName = 'Button'
